import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  NgZone,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { delay, take, takeUntil } from 'rxjs/operators';

import {
  selectOrbiAgentAvailable,
  selectOrbiConversationActive,
  selectResultDetail,
  sendMessage,
} from '@patient-ui/patient-web/store';
import { buildOrbiRequestContext } from '@patient-ui/shared/utils';

import { LayoutService } from '../../../shared/services/layout.service';
import { OrbiPageSettledService } from '../../services/orbi-page-settled.service';
import { OrbiTrackingService } from '../../services/orbi-tracking.service';
import { OrbiMessageInputComponent } from '../orbi-message-input/orbi-message-input.component';

/**
 * Orbi "Ask a question" entry point (PCSA-13157).
 *
 * The persistent, always-available on-page control that opens the AI chat
 * experience. Per the approved design (initial-chat-input / Figma "Agent CTA"),
 * the entry point is an INPUT BAR — a sparkle-prefixed "Ask a question" field
 * with the same typing / voice / send affordances as the in-panel input — not
 * a plain button. It reuses `patient-ui-orbi-message-input` (PCSA-13160) so the
 * three states (idle sparkle+mic, typing send-arrow, "Listening…") stay in one
 * component and never diverge.
 *
 * Placement: mounted ONCE at the layout level (`PortalLayoutComponent`), not
 * per page. It is a `position: fixed` bar (see the component SCSS) — top-right
 * on desktop/tablet, bottom-docked on mobile — so it stays visible while the
 * user scrolls (PCSA-13157 AC2), never displaces page content (out of flow),
 * and lands in the same spot on every authenticated portal page regardless of
 * that page's markup. The one thing a fixed bar cannot know is where the page
 * footer is, so on mobile this component measures it — see
 * `measureFooterOverlap`.
 *
 * Self-contained: it self-gates on agent availability (`enabled$` — Split flag
 * AND identity verification, PCSA-13452; hidden when either is false) and, on
 * submit, opens the chat panel AND dispatches the
 * message itself (send-inline-and-open) so a page can use it with zero wiring.
 * It also hides while the panel is open so it isn't covered by the panel.
 * `ask` is still emitted for hosts that want to observe submissions.
 *
 * Once a conversation is active, the bar changes role (PCSA-13441): selecting it
 * no longer starts a new question but re-opens that conversation in the panel,
 * sending nothing. See `onActivate`.
 */
@Component({
  selector: 'patient-ui-orbi-entry-button',
  templateUrl: './orbi-entry-button.component.html',
  styleUrls: ['./orbi-entry-button.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrbiEntryButtonComponent implements OnInit, OnDestroy {
  /** `media-breakpoint-down(lg)` — the breakpoint at which the bar docks. */
  private static readonly DOCKED_QUERY = '(max-width: 991.98px)';
  /** The layout's page footer (`portal-layout.component.html`). */
  private static readonly FOOTER_SELECTOR = 'footer';
  /** The bar itself, when it is rendered at all — see `measureFooterOverlap`. */
  private static readonly BAR_SELECTOR = '.orbi-entry';
  /** Read by the mobile `bottom` calc in the component SCSS. */
  private static readonly FOOTER_OVERLAP_PROPERTY = '--orbi-footer-overlap';
  /** Read by the same calc — see `visibleViewportBottom`. */
  private static readonly VIEWPORT_INSET_PROPERTY = '--orbi-viewport-inset';

  /**
   * Emitted (in addition to the internal open+dispatch) when the user submits,
   * for hosts that want to observe it. Optional — pages need not bind it.
   */
  @Output() ask = new EventEmitter<string>();

  /**
   * Show only when the agent is available: the Orbi Split flag is on AND the
   * signed-in patient passed identity verification (PCSA-13452). Because the
   * bar is `position: fixed`, hiding it leaves no gap in the page flow.
   */
  readonly enabled$: Observable<boolean> = this.store.select(
    selectOrbiAgentAvailable
  );

  /** Hide the entry bar while the panel is open (it is covered by the panel). */
  readonly panelOpen$: Observable<boolean> =
    this.layoutService.getOrbiPanelOpen();

  /**
   * Hide the entry bar until the page has stopped resizing after a refresh or
   * navigation, so the panel cannot be opened while the footer is still moving
   * (see `OrbiPageSettledService`).
   */
  readonly pageSettled$: Observable<boolean> = this.pageSettled.settled$;

  @ViewChild(OrbiMessageInputComponent)
  private input?: OrbiMessageInputComponent;

  private restoringFocus = false;

  /** In-flight measurement frame, so a scroll burst costs one layout read. */
  private scrollFrame?: number;
  /** Last value written, to skip redundant style writes. */
  private footerOverlap?: number;
  /** Ditto, for the hidden part of the viewport — see `visibleViewportBottom`. */
  private viewportInset?: number;
  /** Watches the boxes that can move the footer — see `observePageLayout`. */
  private resizeObserver?: ResizeObserver;
  /** The footer currently under observation, so it is only observed once. */
  private observedFooter?: Element;
  private readonly destroyed = new Subject<void>();

  constructor(
    private readonly store: Store,
    private readonly router: Router,
    private readonly layoutService: LayoutService,
    private readonly hostRef: ElementRef<HTMLElement>,
    private readonly zone: NgZone,
    private readonly tracking: OrbiTrackingService,
    private readonly pageSettled: OrbiPageSettledService
  ) {}

  ngOnInit(): void {
    this.layoutService
      .getOrbiEntryFocusRequest()
      .pipe(delay(0), takeUntil(this.destroyed))
      .subscribe(() => this.restoreFocus());
    // OUTSIDE Angular, passively — the same treatment `orbi-panel` gives its own
    // footer measurement, and for the same reason: this fires for the whole
    // duration of every scroll gesture on every portal page, and an in-zone
    // listener would run app-wide change detection on each event. Nothing here
    // touches a binding — the measurement is written straight to a CSS custom
    // property — so no change detection is needed at all. The observer is created
    // in here too: zone.js patches ResizeObserver, so where it is constructed is
    // what decides whether its callbacks schedule change detection.
    this.zone.runOutsideAngular(() => {
      window.addEventListener('scroll', this.onPageScroll, { passive: true });
      window.addEventListener('resize', this.onPageScroll, { passive: true });
      // The VISUAL viewport as well, because it is the only one of the two that
      // reports the browser's own chrome coming and going: a mobile URL bar
      // sliding away, or the on-screen keyboard opening over the bar, changes
      // what the patient can see WITHOUT changing `window.innerHeight` — no
      // `resize` fires for either. See `visibleViewportBottom`.
      window.visualViewport?.addEventListener('resize', this.onPageScroll);
      window.visualViewport?.addEventListener('scroll', this.onPageScroll);
      this.observePageLayout();
      // First measurement on the NEXT frame, not here: this component is created
      // while the layout's own template is still being built, so the footer is
      // either not in the DOM yet or is an empty element that
      // `patient-ui-portal-footer` has not filled in — its rect is not final
      // either way, and on a page that never scrolls nothing would correct it.
      this.onWindowScroll();
    });
  }

  ngOnDestroy(): void {
    // Registered by hand rather than via @HostListener, so nothing tears these
    // down for us; a queued frame closes over the host element and must not
    // outlive it either.
    window.removeEventListener('scroll', this.onPageScroll);
    window.removeEventListener('resize', this.onPageScroll);
    window.visualViewport?.removeEventListener('resize', this.onPageScroll);
    window.visualViewport?.removeEventListener('scroll', this.onPageScroll);
    this.resizeObserver?.disconnect();
    this.resizeObserver = undefined;
    this.observedFooter = undefined;
    if (this.scrollFrame !== undefined) {
      cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = undefined;
    }
    this.destroyed.next();
    this.destroyed.complete();
  }

  private restoreFocus(): void {
    if (!this.input) {
      return;
    }
    this.restoringFocus = true;
    this.input.focus();
    this.restoringFocus = false;
  }

  /**
   * Re-measure when the page's LAYOUT changes, not only when it scrolls (QA,
   * 08/17: on /portal/appointments/book/patient-selection the bar was drawn on
   * top of the footer).
   *
   * Scroll and resize alone are not enough, because a portal page that fits the
   * viewport fires neither: its footer is already on screen when the page renders
   * and there is nothing left to scroll, so the mount-time measurement is the only
   * one that ever runs — and that one is too early to be right. Every other way
   * the footer can move is a resize of one of two boxes: the CONTENT WRAPPER this
   * bar is mounted in (a route change swapping the page under a bar that outlives
   * it, content arriving from an API, an accordion opening) or the footer itself
   * (its own first render). So both are observed.
   *
   * The wrapper, `.main-content-wrapper`, and not the document: `ResizeObserver`
   * reports a box's own SIZE, never its position, so the box to watch is the one
   * whose height IS the footer's offset — and on these layouts the document is
   * not it. The shell's host is `d-flex flex-column vh-100`
   * (`PortalLayoutComponent`), an EXACT viewport height, so `body`'s box stays at
   * 100vh however tall the page's content grows; watching it reported nothing on
   * the pages that need this most. /portal/invoices/billing opens on a "Loading…"
   * placeholder and roughly doubles in height when the invoices land, and until
   * the patient scrolled, the bar kept the overlap it measured on its first frame
   * — taken while the page was still empty and its footer still up near the fold
   * (design review 08/27). `body` is observed as well, for any layout whose page
   * box does grow with its content.
   *
   * No feedback loop: all a measurement does is write a custom property on this
   * host, which below lg is `position: fixed` — out of flow, so it cannot resize
   * anything — and above lg is not measured at all.
   */
  private observePageLayout(): void {
    if (typeof ResizeObserver === 'undefined') {
      // Unsupported (or jsdom): scroll + resize still apply.
      return;
    }
    this.resizeObserver = new ResizeObserver(this.onPageScroll);
    const wrapper = this.hostRef.nativeElement.parentElement;
    if (wrapper) {
      this.resizeObserver.observe(wrapper);
    }
    const body = this.hostRef.nativeElement.ownerDocument?.body;
    if (body) {
      this.resizeObserver.observe(body);
    }
  }

  /**
   * Start watching the footer, once there is one to watch. Deferred out of
   * `observePageLayout` because the footer does not exist when this bar mounts —
   * the layout renders it afterwards — and its first layout, from nothing to full
   * height, is precisely the change that has to be caught.
   */
  private observeFooter(footer: Element): void {
    if (!this.resizeObserver || this.observedFooter === footer) {
      return;
    }
    if (this.observedFooter) {
      this.resizeObserver.unobserve(this.observedFooter);
    }
    this.observedFooter = footer;
    this.resizeObserver.observe(footer);
  }

  /** Arrow property so add/removeEventListener see one stable reference. */
  private readonly onPageScroll = (): void => this.onWindowScroll();

  /**
   * Where the BOTTOM of what the patient can see falls, in the coordinate space
   * the docked bar is positioned in.
   *
   * `position: fixed` resolves `bottom` against the fixed element's containing
   * block — the layout viewport — and on mobile that box is deliberately BIGGER
   * than the window: it is sized for the browser's chrome scrolled away, so
   * `100vh` and a bottom-anchored `fixed` box both extend under (or past) the
   * URL bar / toolbar while it is showing. Emulating an iPhone 16 Pro Max in
   * Chrome, `window.innerHeight` — and the box `bottom: 16px` resolves in —
   * reports 991px while only 956px of it is on screen, so the dock landed with
   * its bottom edge 19px BELOW the bottom of the screen and the lower half of
   * the bar was cut off (design review 08/27, Billing on a 440x956 phone). Same
   * arithmetic on a real phone with a bottom toolbar: the bar hides behind it.
   *
   * `window.visualViewport` is the one API that reports the part actually on
   * screen, so the distance from `innerHeight` down to this is how much of the
   * dock's own containing block is out of sight — published as
   * `--orbi-viewport-inset` and taken back off the dock in the SCSS. Zero on a
   * desktop browser, where the two boxes are the same.
   *
   * `offsetTop` is part of the answer and not an afterthought: the visible box
   * is not always flush with the TOP of the layout viewport, and the amount it
   * has slid down is exactly the amount that is NOT missing off the bottom. The
   * on-screen keyboard is the case that makes this visible (QA, 08/31, iOS
   * Safari on a Result Details page): the keyboard does not resize the layout
   * viewport, so `innerHeight` does not move, but it takes ~300px off
   * `visualViewport.height` AND slides the visible box down the layout viewport
   * to bring the focused field up — and iOS draws `fixed` boxes in layout
   * coordinates, so it is already moving the dock up the screen by `offsetTop`
   * on its own. Reading the height alone therefore lifted the bar a second time,
   * by the whole height of the keyboard, and the entry point shot up the page to
   * the top of the screen while the patient typed into it. `offsetTop + height`
   * is the visible box's bottom EDGE, which corrects both regimes with one
   * number: the browser chrome (the box is short but still at the top,
   * `offsetTop` 0) and the keyboard (short AND slid down, so the two terms
   * cancel to roughly the pre-keyboard inset and the bar stays put — 16px above
   * the keyboard, which is the bottom of what the patient can see).
   *
   * It is also the right baseline for the footer measurement in
   * `measureFooterOverlap`: `getBoundingClientRect()` is layout-viewport
   * relative, so comparing it against a height alone would, with the keyboard
   * up, put the footer's top edge a keyboard's worth of pixels off.
   *
   * The height is scaled by `visualViewport.scale`, which converts the visual
   * viewport's CSS px back into the LAYOUT px `bottom` is resolved in. Without
   * it, pinch-zoom (a smaller visible box, same browser chrome) would read as a
   * giant inset and throw the bar up the screen. `offsetTop` needs no such
   * conversion — it is already reported in the layout viewport's own px. `min`
   * with `innerHeight` keeps the inset at or above zero on the engines that
   * report the SMALLER of the two boxes in `innerHeight` (iOS Safari), where
   * there is nothing to correct.
   */
  private visibleViewportBottom(): number {
    const viewport = window.visualViewport;
    if (!viewport) {
      return window.innerHeight;
    }
    return Math.min(
      window.innerHeight,
      Math.round(viewport.offsetTop + viewport.height * viewport.scale)
    );
  }

  /**
   * Coalesce a scroll/resize burst onto the next animation frame.
   *
   * A frame, not a trailing debounce: the bar has to keep pace with the footer
   * while the page is moving, or it lags behind and paints over the very links
   * the gap exists to keep clear. Scroll events can outpace the compositor, and
   * one frame is the shortest wait that still collapses a burst into a single
   * layout read — which is also the most the user can see.
   */
  onWindowScroll(): void {
    if (this.scrollFrame !== undefined) {
      return;
    }
    this.scrollFrame = requestAnimationFrame(() => {
      this.scrollFrame = undefined;
      this.measureFooterOverlap();
    });
  }

  /**
   * Publish how much of the page footer is on screen, so the docked bar can stop
   * 64px above it instead of covering it (PCSA-13312, 08/14 Vibe item 10) — and
   * how much of the dock's own containing block is off screen, so it docks 16px
   * above the bottom the PATIENT sees (see `visibleViewportBottom`).
   *
   * `getBoundingClientRect().top` is the footer's distance from the top of the
   * viewport, so `visible height - top` is how far the footer has risen into it:
   * negative while it is still below the fold, growing to the footer's height at
   * the very bottom of the page. That single number is the whole measurement —
   * the SCSS turns it into a lift off the dock (`-max(0, overlap + 64px - 16px)`),
   * which keeps both spacing tokens in the stylesheet and means this method has no
   * opinion about how big either gap is.
   *
   * Deliberately NOT clamped at the top end. On a page whose footer is taller
   * than the viewport the bar rides up with the footer's top edge and eventually
   * scrolls off screen, which is the behavior "anchored above the footer" asks
   * for — clamping it would put the bar back over the footer's links.
   *
   * Skipped entirely above lg, where the bar is pinned to the right rail and the
   * property is unused: no reason to read layout on every frame there. Skipped
   * too when the value has not moved, which is the common case (scrolling
   * anywhere but the foot of the page).
   *
   * Skipped as well when there is no BAR to position — the agent is unavailable
   * for this patient, or the panel is open over it. The layout mounts this
   * component unconditionally (unlike the panel, which is `*ngIf`-ed on
   * availability) precisely so that a patient who verifies their identity
   * mid-session gets the bar without a reload — which means that for everyone the
   * agent is NOT available to, every scroll frame would otherwise measure a footer
   * on behalf of a bar that does not exist. Cheaper to ask whether the bar is
   * there than to keep the answer in a second subscription.
   */
  measureFooterOverlap(): void {
    if (
      !this.hostRef.nativeElement.querySelector(
        OrbiEntryButtonComponent.BAR_SELECTOR
      )
    ) {
      // Forget the last value with it: the footer is free to move while the bar is
      // away, so the next measurement has to be allowed to write even if it
      // happens to match what was published before. Take the PUBLISHED one down
      // too — the stylesheet reads that property whether or not anything is
      // measuring, so a value left on the host would place the bar, for the frame
      // it reappears on, from geometry that may be several page-loads old.
      this.footerOverlap = undefined;
      this.viewportInset = undefined;
      this.hostRef.nativeElement.style.removeProperty(
        OrbiEntryButtonComponent.FOOTER_OVERLAP_PROPERTY
      );
      this.hostRef.nativeElement.style.removeProperty(
        OrbiEntryButtonComponent.VIEWPORT_INSET_PROPERTY
      );
      return;
    }
    if (!window.matchMedia(OrbiEntryButtonComponent.DOCKED_QUERY).matches) {
      return;
    }
    // One read of the visible viewport, two uses: it offsets the dock itself, and
    // it is what the footer's rise is measured against. Published whether or not
    // this page HAS a footer — the dock needs it either way.
    const visibleBottom = this.visibleViewportBottom();
    const inset = window.innerHeight - visibleBottom;
    if (Number.isFinite(inset) && inset !== this.viewportInset) {
      this.viewportInset = inset;
      this.hostRef.nativeElement.style.setProperty(
        OrbiEntryButtonComponent.VIEWPORT_INSET_PROPERTY,
        `${inset}px`
      );
    }
    const footer = this.hostRef.nativeElement.ownerDocument?.querySelector(
      OrbiEntryButtonComponent.FOOTER_SELECTOR
    );
    if (!footer) {
      return;
    }
    this.observeFooter(footer);
    const overlap = Math.round(
      visibleBottom - footer.getBoundingClientRect().top
    );
    // Publish a NUMBER or nothing at all. Anything else reaches the stylesheet as
    // a token that is not a length (`NaNpx`), which makes the declaration reading
    // it invalid at computed-value time and silently costs the bar its footer
    // clearance for as long as the value sits there. Holding the last good
    // measurement is the better failure — the next scroll or layout change
    // re-measures anyway.
    if (!Number.isFinite(overlap) || overlap === this.footerOverlap) {
      return;
    }
    this.footerOverlap = overlap;
    this.hostRef.nativeElement.style.setProperty(
      OrbiEntryButtonComponent.FOOTER_OVERLAP_PROPERTY,
      `${overlap}px`
    );
  }

  /**
   * User submitted a question from the entry bar: open the panel AND dispatch
   * the message so the answer is already loading when the panel appears. Page
   * context is derived from the current route; the effect (PCSA-13164) owns the
   * session id, Split gate, and transport.
   */
  onAsk(text: string): void {
    this.tracking.trackMessageSent(this.input?.inputMethod ?? 'typed');
    this.layoutService.setOrbiHistoryOpen(false);
    this.layoutService.setOrbiPanelOpen(true);
    // Build the request context LIVE at send time (PCSA-13222): the nav label(s)
    // for the current route plus, on the results detail page, the accession the
    // portal already loaded (read as a one-shot store snapshot — no new fetch).
    this.store
      .select(selectResultDetail)
      .pipe(take(1))
      .subscribe((resultDetail) =>
        this.store.dispatch(
          sendMessage({
            userMessage: text,
            requestContext: buildOrbiRequestContext(this.router.url, {
              resultDetail,
            }),
          })
        )
      );
    this.ask.emit(text);
  }

  /**
   * User SELECTED the entry field — focused it, or tapped the Orbi mark, which
   * focuses it (PCSA-13441).
   *
   * With no conversation yet this is a no-op: the field keeps focus and the user
   * types or dictates as before. Once a conversation is ACTIVE (a message was
   * sent this session, or one was resumed from history) the same gesture opens
   * that conversation in the panel — with NO message sent — and the thread
   * renders scrolled to its latest turn. Opening the panel also hides this bar,
   * so focus does not linger on a field the user can no longer see.
   *
   * A one-shot store read (same idiom as `onAsk`) rather than a subscription:
   * this is a discrete gesture, so the answer is only needed at this instant and
   * there is no stream to unsubscribe on destroy.
   */
  onActivate(): void {
    if (this.restoringFocus) {
      return;
    }
    this.store
      .select(selectOrbiConversationActive)
      .pipe(take(1))
      .subscribe((active) => {
        this.tracking.trackAgentOpened(active ? 'active' : 'new');
        if (!active) {
          return;
        }
        // The thread, not history — the user asked for the conversation itself.
        this.layoutService.setOrbiHistoryOpen(false);
        this.layoutService.setOrbiPanelOpen(true);
      });
  }

  /**
   * User tapped the entry field's history affordance (PCSA-13312): open the chat
   * panel onto the conversation-history view. The history view itself ships with
   * PCSA-13169; here we carry the "start on history" intent so it presents that
   * view the moment 13169 lands (and the panel opens correctly in the meantime).
   */
  onHistory(): void {
    this.layoutService.setOrbiHistoryOpen(true);
    this.layoutService.setOrbiPanelOpen(true);
  }
}
